// Hook que carga el perfil profesional del kine logueado: nombre,
// matrícula, email. Usado por ConfiguracionKine.tsx (card "Perfil
// profesional").
//
// kine_id resuelto via useCurrentKineId() (string | null). Early
// return si null — el componente queda en loading. Post-B8 ya no
// hay fallback a TEMP, el hook devuelve userId real o null limpio.
//
// La columna `telefono` NO existe en tabla `kines` (ver
// supabase/migrations/0001_initial_schema.sql:11-19), por eso este
// hook no la trae. La columna `especialidad` sí existe pero queda
// fuera de scope hasta que producto pida exhibirla.

import { useEffect, useState } from "react";
import { supabase } from "../client";
import { useCurrentKineId } from "@/app/components/auth/AuthProvider";

export interface KineProfile {
  nombre: string;
  /** Matrícula profesional (ej. "MN 12345"). Nullable en DB —
   *  paciente seed la tiene NULL. */
  matricula: string | null;
  email: string;
}

interface State {
  data: KineProfile | null;
  error: string | null;
  loading: boolean;
}

export function useKineProfile(): State {
  const kineId = useCurrentKineId();
  const [state, setState] = useState<State>({
    data: null,
    error: null,
    loading: true,
  });

  useEffect(() => {
    // Espera a que useCurrentKineId resuelva (null durante auth loading)
    // para evitar el race con TEMP fallback.
    if (!kineId) return;

    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from("kines")
        .select("nombre, matricula, email")
        .eq("id", kineId)
        .maybeSingle()
        .returns<KineProfile>();

      if (cancelled) return;

      if (error) {
        console.warn("[useKineProfile] error cargando perfil", error);
        setState({ data: null, error: error.message, loading: false });
        return;
      }

      if (!data) {
        setState({
          data: null,
          error: "No encontramos tu perfil",
          loading: false,
        });
        return;
      }

      setState({ data, error: null, loading: false });
    })();

    return () => {
      cancelled = true;
    };
  }, [kineId]);

  return state;
}
